import { JSX } from 'preact';
import { useCallback, useEffect, useMemo, useRef } from 'preact/hooks';

import { Modal } from '../UNSAFE_Modal';
import { WindowOverlay } from '../UNSAFE_WindowOverlay';
//Temporal import.
import { useSheetSwipe } from './useSheetSwipe';
import { focusWithin } from '../utils/PRIVATE_tabbableUtils';

import { sheetMenuStyles } from './themes/SheetStyles.css';
import { mergeRefs } from '#utils/PRIVATE_refUtils';
import { AnimationStates, useAnimationStatus } from '#hooks/PRIVATE_useAnimationStatus';
import { SLIDE_DOWN_XLARGE, SLIDE_UP_XLARGE, DURATION_LARGE } from '#utils/PRIVATE_animationUtils';

/*
  pointerDismissed: Swipedown and tapScrim
  keyboardDismissed: "Escape" | "Tab"
*/
export type CloseDetail = { reason: 'pointerDismissed' | 'keyboardDismissed' };

type IntrinsicProps = Pick<JSX.HTMLAttributes<HTMLDivElement>, 'children'>;

type SheetProps = IntrinsicProps & {
  onClose?: (detail: CloseDetail) => void;
  isOpen?: boolean;
  initialFocus?: 'none' | 'firstFocusable';
};

const animationStates: AnimationStates = {
  opening: {
    ...SLIDE_UP_XLARGE,
    options: {
      duration: DURATION_LARGE
    }
  },
  closing: {
    ...SLIDE_DOWN_XLARGE,
    options: {
      duration: DURATION_LARGE
    }
  }
};

export const Sheet = ({ children, onClose, isOpen = false, initialFocus = 'none' }: SheetProps) => {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Initial focus going to be set after animation once opening animation is added
  useEffect(() => {
    if (isOpen && initialFocus === 'firstFocusable') {
      const sheetElement = sheetRef.current;
      if (sheetElement) {
        focusWithin(sheetElement);
      }
    }
  }, [isOpen, initialFocus]);
  const handlePointerDismissed = useCallback(() => {
    onClose?.({ reason: 'pointerDismissed' });
  }, [onClose]);

  // TODO: Use useSwipe when it works on components that could scroll. Important to notice all components that have a
  // sheet will use this comp and this comp is going to use useSwipe. So comps that have their own impl (menu right now),
  // will stop having its own implementation
  const { swipeProps } = useSheetSwipe(handlePointerDismissed);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }
      switch (event.code) {
        case 'Tab':
        case 'Escape':
          onClose?.({ reason: 'keyboardDismissed' });
        default:
          break;
      }
    },
    [onClose]
  );
  const { setAnimationElementRef, status } = useAnimationStatus<HTMLElement>({
    isOpen,
    isAnimatedOnMount: false,
    animationStates
  });

  useEffect(() => {
    if (status === 'opening') {
      if (initialFocus === 'firstFocusable') {
        const dropdownElement = sheetRef.current;
        if (dropdownElement) {
          focusWithin(dropdownElement);
        }
      }
    }
  }, [status, initialFocus]);

  const stableRef = useMemo(
    () => mergeRefs(setAnimationElementRef, sheetRef),
    [setAnimationElementRef]
  );

  const isMounted = status !== 'unmounted';

  return (
    <>
      {isMounted ? (
        <Modal isOpen={isMounted} onBackdropClick={handlePointerDismissed}>
          <WindowOverlay placement="bottom">
            <div
              {...swipeProps}
              ref={stableRef}
              class={sheetMenuStyles.sheetMenuContainerWrapper}
              style={{ maxHeight: `calc(100vh - ${TOP_OFFSET}px)` }}
              onKeyDown={handleKeyDown}>
              {children}
            </div>
          </WindowOverlay>
        </Modal>
      ) : null}
    </>
  );
};

const TOP_OFFSET = 96;
